import React, { createContext, useContext, useEffect, useLayoutEffect } from 'react';
import { LSKEY_INITIAL_URL } from 'src/utils/auth';
import { UserOptionType, Variable } from 'src/api/types';
import { featuresMap } from './util';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { setCurrencySymbol } from 'src/utils/misc';
import {
  FlatOrganisationsQuery,
  ProfileQuery,
  useFlatOrganisationsLazyQuery,
  useProfileLazyQuery,
} from 'src/graphql/_generated';
import useIsRealUser from './useIsRealUser';
import { ApolloError } from '@apollo/client';
import { useAuth0 } from '@auth0/auth0-react';
import { initializeTokenService, clearTokenService } from 'src/utils/tokenService';
import { makeLogout } from 'src/utils/auth';

interface AuthContextInterface {
  usersOptios: UserOptionType[];
  isSignedIn: boolean;
  isLoading: boolean;
  appLoaded: boolean;
  isPlatformAdmin: boolean;
  isTscsRequiredToAccept: boolean;
  profile: ProfileQuery['profile'] | undefined;
  flatOrganisations: FlatOrganisationsQuery['flatOrganisations'];
  features: ReturnType<typeof featuresMap>;
  variables: Variable[];
  workspaceId: string;
  orgId: string;
  getRealUserId: (email?: string | null) => string | null;
  error: ApolloError | undefined;
}

const AuthContext = createContext<Partial<AuthContextInterface>>({});

const AuthProvider = (props: React.PropsWithChildren) => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: auth0Loading, getAccessTokenSilently, logout: logoutAuth0 } = useAuth0();

  const [getProfile, { data: profile, error: profileError }] = useProfileLazyQuery();
  const [getFlatOrganisations, { data: flatOrganisations, error: flatOrganisationsError }] =
    useFlatOrganisationsLazyQuery();
  const { getRealUserId } = useIsRealUser({
    workspaceId: profile?.profile.activeWorkspace?.id,
    orgId: profile?.profile.activeOrganisation?.id,
  });

  const performFullLogout = () => {
    makeLogout({ noEvents: true });
    logoutAuth0({
      logoutParams: { returnTo: `${window.location.protocol}//${window.location.host}/auth` },
    });
  };

  useEffect(() => {
    if (isAuthenticated && !auth0Loading) {
      initializeTokenService(getAccessTokenSilently, performFullLogout);
    } else if (!isAuthenticated && !auth0Loading) {
      clearTokenService();
    }
  }, [isAuthenticated, auth0Loading, getAccessTokenSilently, logoutAuth0]);

  useEffect(() => {
    if (isAuthenticated && !auth0Loading) {
      getProfile().then(({ data }) => {
        if (data?.profile?.user) {
          Sentry.setUser({
            id: data.profile.user.id,
            organisation_id: data.profile.activeOrganisation?.id,
            workspace_id: data.profile.activeWorkspace?.id,
          });
          setCurrencySymbol(data?.profile?.activeWorkspace?.currency);
          getFlatOrganisations({ variables: { memberEmail: data.profile.user.email } });
        }
      });
    }
  }, [isAuthenticated, auth0Loading, getProfile, getFlatOrganisations]);

  useLayoutEffect(() => {
    if (!auth0Loading && isAuthenticated && profile?.profile.activeWorkspace) {
      const initialUrl = window.localStorage.getItem(LSKEY_INITIAL_URL);

      if (initialUrl) {
        const invites = profile.profile?.user?.invites || [];
        if (
          profile.profile?.user?.workspaces.filter((el) => el.actionsOnThisWorkspace?.canAccess).length === 1 &&
          invites.length === 0
        ) {
          navigate(initialUrl || '/risk-assessments', { replace: true });
        } else {
          navigate(initialUrl || '/home', { replace: true });
        }

        window.localStorage.removeItem(LSKEY_INITIAL_URL);
      }
    }
  }, [auth0Loading, isAuthenticated, profile, navigate]);

  const features = featuresMap(profile?.profile?.features || []);

  return (
    <AuthContext.Provider
      value={{
        usersOptios: profile?.profile.activeWorkspace?.owners || [],
        isSignedIn: isAuthenticated,
        isLoading: auth0Loading,
        appLoaded: Boolean(!auth0Loading && isAuthenticated && profile && flatOrganisations),
        error: profileError || flatOrganisationsError,
        isPlatformAdmin: profile?.profile?.permissions.isPlatformAdmin,
        isTscsRequiredToAccept:
          profile?.profile?.terms.status === 'REQUIRED' ||
          profile?.profile?.terms.status === 'NOT_AUTHORISED_TO_ACCEPT',
        profile: profile?.profile,
        flatOrganisations: flatOrganisations?.flatOrganisations || [],
        features,
        variables:
          profile?.profile.activeWorkspace?.variables?.map(
            (el) =>
              ({
                id: el.id!,
                name: el.name!,
                value: el.value!,
                currency: el.currency,
                isInUse: el.isInUse as string[],
                workspace_variable: !!el.workspaceVariable,
                alias: el.alias || null,
                isGlobal: !!el.isGlobal,
              }) satisfies Variable,
          ) || [],
        workspaceId: profile?.profile.activeWorkspace?.id,
        orgId: profile?.profile.activeOrganisation?.id,
        getRealUserId,
      }}
      {...props}
    />
  );
};

const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within a AuthProvider');
  }
  return context as AuthContextInterface;
};

export { AuthProvider, useAuth };
